import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../../comun/services/grpc/mapConnectError";
import {
  MonitoreoSesionArbolPuntoItem,
  MonitoreoSesionDetalleLoteItem,
  MonitoreoSesionService,
} from "../../../../generated/grpc/monitoreo_sesion_pb";
import {
  LoteSesionDetalleResponse,
  PuntoSesionDetalleResponse,
  SesionDetalleResponse,
} from "../response/SesionDetalleResponse";

/** Cuánto se reusa el último detalle pedido de una sesión antes de volver a
 *  pedirlo al server (pedido explícito, 2026-09-15) — sin delta real: el
 *  endpoint no acepta `ultimaSincronizacion`, así que un refresh acá siempre
 *  trae el detalle completo, no un incremental. */
const CACHE_MS = 5 * 60_000;

/** Variante gRPC-Web de `SesionDetalleGetService` (ver ese archivo): detalle
 *  de una sesión de monitoreo, con los lotes e incidencias ya promediadas por
 *  el server. Sin delta -el endpoint no tiene `ultimaSincronizacion` ni lo
 *  tenía en REST-, pero cachea en memoria el último detalle de cada sesión
 *  por `CACHE_MS`. `id` no viaja -confirmado sin uso real, el llamador ya
 *  conoce el `sesionId`- y se rellena vacío para mantener el shape REST. */
class SesionDetalleGrpcService {
  /** Último detalle pedido por sesión, con cuándo se pidió. */
  private readonly _cache = new Map<string, { value: SesionDetalleResponse; fetchedAt: number }>();
  /** Peticiones en curso por sesión, para deduplicar llamadas concurrentes. */
  private readonly _inFlight = new Map<string, Promise<SesionDetalleResponse>>();

  /** Detalle de la sesión indicada. */
  async getAsync(sesionId: string): Promise<SesionDetalleResponse> {
    const cacheado = this._cache.get(sesionId);
    if (cacheado && Date.now() - cacheado.fetchedAt < CACHE_MS) {
      return cacheado.value;
    }

    let peticion = this._inFlight.get(sesionId);
    if (!peticion) {
      peticion = this._fetchAsync(sesionId)
        .then((value) => {
          this._cache.set(sesionId, { value, fetchedAt: Date.now() });
          return value;
        })
        .finally(() => this._inFlight.delete(sesionId));
      this._inFlight.set(sesionId, peticion);
    }

    return peticion;
  }

  private async _fetchAsync(sesionId: string): Promise<SesionDetalleResponse> {
    const client = createClient(MonitoreoSesionService, grpcTransportProvider.get());

    let detalle;
    try {
      detalle = await client.getDetalle({ sesionId });
    } catch (error) {
      throw mapConnectError(error);
    }

    return {
      id: "",
      fincaNombre: detalle.fincaNombre,
      semana: detalle.semana,
      fecha: detalle.fecha,
      tipo: detalle.tipo,
      monitor: detalle.monitor ?? null,
      agronomo: detalle.agronomo ?? null,
      isFinalizada: detalle.isFinalizada,
      lotes: detalle.lotes.map(toLoteSesionDetalleResponse),
      puntos: detalle.puntos.map(toPuntoSesionDetalleResponse),
    };
  }
}

/** Singleton del servicio gRPC de detalle de sesión. */
export const sesionDetalleGrpcService = new SesionDetalleGrpcService();

function toLoteSesionDetalleResponse(
  lote: MonitoreoSesionDetalleLoteItem,
): LoteSesionDetalleResponse {
  return {
    id: lote.id,
    nombre: lote.nombre,
    areaHa: lote.areaHa,
    totalArboles: lote.totalArboles,
    muestra: lote.muestra,
    fenologia: lote.fenologia ?? null,
    fenologiaDistribucion: lote.fenologiaDistribucion.map((f) => ({ estado: f.estado, arboles: f.arboles })),
    observaciones: lote.observaciones ?? null,
    inc: lote.inc,
    afectados: lote.afectados,
    referencias: lote.referencias,
    cultivoId: lote.cultivoId,
    cultivoNombre: lote.cultivoNombre,
  };
}

function toPuntoSesionDetalleResponse(
  punto: MonitoreoSesionArbolPuntoItem,
): PuntoSesionDetalleResponse {
  return {
    loteId: punto.loteId,
    numeroArbol: punto.numeroArbol,
    lat: punto.lat,
    lng: punto.lng,
    fechaCaptura: punto.fechaCaptura,
    detalles: punto.detalles,
  };
}
